// src/components/dashboard/NeurodiversityProfile.jsx (プロファイリング機能強化版)
import React, { useMemo } from 'react';
import { SlidersHorizontal, BrainCircuit } from 'lucide-react';

const TraitMeter = ({ trait }) => {
  const labels = trait.label.split(' vs ');
  const leftLabel = labels[0] || '';
  const rightLabel = labels[1] || '';
  return (
    <div>
      <div className="flex justify-between items-center mb-1 text-sm font-medium text-gray-600 px-1">
        <span>{leftLabel}</span>
        <span>{rightLabel}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-4 relative overflow-hidden">
        <div 
          className="bg-gradient-to-r from-cyan-400 to-blue-500 h-4 rounded-full transition-all duration-500 ease-out" 
          style={{ width: `${trait.value}%` }}
        ></div>
        <div className="absolute top-0 left-1/2 w-0.5 h-full bg-white/50"></div>
      </div>
      <p className="text-sm text-gray-700 mt-2 text-center">{trait.description}</p>
    </div>
  );
};

const NeurodiversityProfile = ({ profileData }) => {
    // データなしの場合は空状態を表示
    if (!profileData || !profileData.processingStyle) {
        return (
            <div className="text-center py-12">
                <div className="text-4xl mb-4">🧠</div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">認知特性データがありません</h3>
                <p className="text-gray-500 text-sm">NOBISHIROコンピテンシー診断を受けると、あなたの認知特性が可視化されます。</p>
            </div>
        );
    }

    // ★★★ 新規ロジック：データに基づいて分析サマリーを生成します ★★★
    const summary = useMemo(() => {
        if (!profileData) return null;
        
        const insights = [];
        const { processingStyle, thinkingStyle, attentionPattern } = profileData;

        if (!processingStyle || !thinkingStyle || !attentionPattern) return null;

        if (processingStyle.value > 60) {
            insights.push("全体像から物事を捉え、トップダウンで思考する戦略的なアプローチを得意とします。");
        } else if (processingStyle.value < 40) {
            insights.push("一つ一つの情報を着実に積み上げていく、緻密で丁寧なアプローチが得意です。");
        }

        if (thinkingStyle.value > 60) {
            insights.push("既成概念にとらわれず、多様なアイデアを自由に生み出す発散的思考が強みです。");
        } else if (thinkingStyle.value < 40) {
            insights.push("多くの情報から本質を見抜き、最適な答えを導き出す収束的思考に長けています。");
        }

        if (attentionPattern.value > 60) {
            insights.push("興味関心が広く、複数のタスクを並行して進めるマルチタスク環境で力を発揮します。");
        } else if (attentionPattern.value < 40) {
            insights.push("一度決めたタスクに対して深く集中し、質の高い成果を生み出すことができます。");
        }

        if (insights.length < 2) {
            insights.push("バランスの取れた認知特性を持っており、様々な状況に柔軟に対応できる可能性があります。");
        }

        return insights.join(' ');

    }, [profileData]);

    if (!profileData || Object.keys(profileData).length === 0) {
        return (
            <div className="bg-yellow-50 text-yellow-800 p-6 rounded-lg shadow-md text-center">
                <h3 className="font-semibold text-lg">データがありません</h3>
                <p className="mt-2">このユーザーの認知特性プロファイルデータはまだ登録されていません。</p>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md h-full">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-3">
                <SlidersHorizontal className="w-6 h-6 text-cyan-600" />
                認知特性プロファイル
            </h2>
            <div className="space-y-8 max-w-lg mx-auto">
                <TraitMeter trait={{...profileData.processingStyle, label: "逐次的処理 vs 全体的処理"}} />
                <TraitMeter trait={{...profileData.thinkingStyle, label: "収束的思考 vs 発散的思考"}} />
                <TraitMeter trait={{...profileData.attentionPattern, label: "持続的注意 vs 変動的注意"}} />
            </div>

            {/* ★★★ 新規UI：分析サマリーを表示します ★★★ */}
            {summary && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                     <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <BrainCircuit className="w-5 h-5 text-indigo-500" />
                        あなたの思考の強み
                    </h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-700 leading-relaxed">{summary}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NeurodiversityProfile;